<script setup lang="ts">
import { computed, provide, ref, useTemplateRef } from 'vue';
import { useRouter } from 'vue-router';
import { useElementSize } from '@vueuse/core';
import { ArrowLeftIcon, XMarkIcon } from '@heroicons/vue/24/outline';
import FullPageIcon from '@/components/icons/FullPageIcon.vue';
import { LazyTooltip } from '@/components/ui/tooltip';
import { buildEntityColumns, entityDisplayNameFor } from '@/composables/entityColumns';
import { useRecordLayout } from '@/composables/recordLayout/useRecordLayout';
import { useRecordHeading } from '@/composables/recordLayout/useRecordHeading';
import { RECORD_PANE_NAVIGATOR, type PaneTrail } from '@/composables/recordPaneTrail';
import {
    inspectorComponentFor,
    inspectorExcludeFieldsFor,
    overlayHeaderComponentFor,
} from '@/composables/searchInspectorRegistry';
import { areaEntity, entityGate, nounInSentence, recordPathFor } from '@/navigation/areas';
import { isGateOpen } from '@/navigation/gates';
import CustomizeRecordDialog from '@/components/record/CustomizeRecordDialog.vue';
import RecordMoreMenu from '@/components/record/RecordMoreMenu.vue';
import ModelRecordInspector from '@/components/ModelRecordInspector.vue';
import RecordBody from '@/components/record/RecordBody.vue';
import type { TableColumn } from '@/types/tableTypes';

/**
 * The record a list has selected, drawn beside a card list or below a table.
 * Both places use this one pane, so they read the same: the record's name and
 * top line, then the ⋯ menu, the full-page button and × in the top right
 * corner, then the record's body from the person's layout. The full-page
 * button draws the page the record will fill, as the peeks' full-page button
 * does.
 *
 * It reads the row the list already holds, so live-polled values show at once.
 *
 * A related record clicked inside the pane moves the pane to that record. The
 * header then starts with ← and the trail, such as "Angelica Waelchi III ›
 * Main St Café". Nothing opens on top of the pane. The trail belongs to the
 * list, which clears it when another row is selected.
 * Spec: docs/specs/record-navigation-layers.md, "The pane's trail".
 *
 * A schedule has no body of its own. It is read in its custom list, so for a
 * schedule the pane shows that list's inspector on Schedules.
 */
const CUSTOM_LIST_ENTITY_ID = 'LocationZoneCustomPlaylist';

const props = withDefaults(
    defineProps<{
        /** The list's record type, such as "Account". */
        entityId: string;
        /** The selected row, as the list holds it. */
        row: Record<string, unknown>;
        /** The custom list a selected schedule belongs to, or null for anything else. */
        scheduleListId?: number | null;
        /**
         * Draw the body from the person's layout. A bare test mount has no list
         * route and no layout to load, so it falls back to the entity's inspector.
         */
        useLayout?: boolean;
        /** Every column of the entity, for the fallback inspector. */
        inspectorColumns?: TableColumn[];
        /** Show a × button that closes the pane. */
        closable?: boolean;
        /** Escape closes the pane once its trail is empty, so ×'s tooltip names the key. The table's pane does. */
        escapeCloses?: boolean;
        /**
         * The pane's trail, owned by the list. Without one, a related record
         * clicked in the pane opens the side peek instead.
         */
        trail?: PaneTrail | null;
    }>(),
    { scheduleListId: null, useLayout: true, inspectorColumns: () => [], closable: false, escapeCloses: false, trail: null },
);

const emit = defineEmits<{
    /** Open the selected row's full page. */
    open: [];
    /** Close the pane and clear the list's selection. */
    close: [];
}>();

const router = useRouter();

/** The record the pane has moved to along its trail, or null while it shows the selected row. */
const currentStep = computed(() => props.trail?.current.value ?? null);

if (props.trail) {
    const trail = props.trail;
    provide(RECORD_PANE_NAVIGATOR, {
        isActive: () => true,
        showRecord: (entityId, recordId, options) =>
            trail.push({
                entityId,
                recordId,
                // A friendly name until the record loads: "Custom Playlist #1", not "LocationZoneCustomPlaylist #1".
                title: `${entityDisplayNameFor(entityId)} #${recordId}`,
                ...(options?.initialSection ? { initialSection: options.initialSection } : {}),
            }),
        renameRecord: (entityId, recordId, title) => trail.rename(entityId, recordId, title),
    });
}

const isSchedule = computed(() => currentStep.value === null && props.scheduleListId !== null);
/** The record type the pane shows: the step's, else the custom list for a schedule, else the row's. */
const shownEntityId = computed(() => currentStep.value?.entityId ?? (isSchedule.value ? CUSTOM_LIST_ENTITY_ID : props.entityId));
const shownRecordId = computed(() => currentStep.value?.recordId ?? props.scheduleListId ?? (props.row.id as number | string));
/** The record type whose layout the body and Customize use. A schedule's row uses its own. */
const layoutEntityId = computed(() => currentStep.value?.entityId ?? props.entityId);

const headerComponent = computed(() => overlayHeaderComponentFor(shownEntityId.value));
const fallbackComponent = computed(() => inspectorComponentFor(shownEntityId.value));
const excludeFields = computed(() => inspectorExcludeFieldsFor(props.entityId));
const columns = computed(() => buildEntityColumns(layoutEntityId.value));
const headingNoun = computed(() => areaEntity(layoutEntityId.value)?.noun ?? entityDisplayNameFor(layoutEntityId.value));
const noun = computed(() => nounInSentence(headingNoun.value));

const layout = props.useLayout ? useRecordLayout(() => layoutEntityId.value, columns) : null;
const customizeOpen = ref(false);
const moreMenu = useTemplateRef<InstanceType<typeof RecordMoreMenu>>('moreMenu');

/** The selected row's name, which starts the trail. */
const rowHeading = useRecordHeading(() => props.entityId, () => props.row.id as number | string, () => props.row);

/** The trail's names: the selected row, then each record moved to. The last one is shown. */
const trailNames = computed(() => [rowHeading.value.title, ...(props.trail?.steps.value ?? []).map((step) => step.title)]);

/** The full page of a record the pane moved to, when it has one the person may open. */
const stepPagePath = computed(() => {
    const step = currentStep.value;
    if (!step || !areaEntity(step.entityId) || !isGateOpen(entityGate(step.entityId))) return null;
    return recordPathFor(step.entityId, step.recordId);
});
const hasFullPage = computed(() => currentStep.value === null || stepPagePath.value !== null);

function openFullPage() {
    if (currentStep.value === null) {
        emit('open');
    } else if (stepPagePath.value) {
        void router?.push(stepPagePath.value);
    }
}

/**
 * The room the header's first line leaves at its right for the buttons: their
 * width, and the same gap that used to sit between the buttons and the title.
 * With a trail, the buttons sit on the trail's line instead.
 */
const ACTIONS_GAP_PX = 12;
const actionsElement = useTemplateRef<HTMLElement>('actionsElement');
const { width: actionsWidth } = useElementSize(actionsElement, undefined, { box: 'border-box' });
const actionsReservedWidth = computed(() => (actionsWidth.value > 0 ? Math.ceil(actionsWidth.value) + ACTIONS_GAP_PX : 0));

/** For a schedule the list is named only by its id. The header and the inspector fetch the list. */
const fallbackProps = computed(() =>
    isSchedule.value
        ? { row: null, locationZoneCustomPlaylistId: props.scheduleListId, entityId: CUSTOM_LIST_ENTITY_ID }
        : { row: props.row, columns: props.inspectorColumns, excludeFields: excludeFields.value, entityId: props.entityId },
);

/** Keyed by the record shown, so two schedules of one list keep one inspector. */
const recordKey = computed(() => `${shownEntityId.value}-${String(shownRecordId.value)}`);

const iconButtonClass =
    'inline-flex size-7 shrink-0 items-center justify-center rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground';
</script>

<template>
  <!-- The same header the peeks show, so the pane and the peeks read alike: the
       record's name and top line on top, then the body, which never repeats them. -->
  <div class="flex flex-col gap-4" data-testid="record-pane" :data-shown-record="recordKey">
    <!-- The ⋯ menu, the full-page button and × sit in the top right corner, level with the
         top line, as the page and the peeks put their actions. The header itself runs the
         pane's full width, so the values at the right of its lower rows, such as a
         playlist's counts, line up with the last button. Only the header's first line
         leaves room for the buttons. It reads that room from
         `--record-header-actions-width`, which is set here and nowhere else. With a
         trail, the trail is the first line, and the header below it needs no room. -->
    <div
      class="relative"
      :style="{ '--record-header-actions-width': currentStep ? '0px' : `${actionsReservedWidth}px` }"
      data-testid="split-pane-header"
    >
      <!-- ← and the trail, once the pane has moved on from the selected row. Long names
           are cut short, and hovering one shows it whole. -->
      <nav
        v-if="currentStep"
        aria-label="Pane trail"
        class="mb-2 flex h-7 min-w-0 items-center gap-1 text-xs text-muted-foreground"
        :style="{ paddingRight: `${actionsReservedWidth}px` }"
        data-testid="record-pane-trail"
      >
        <LazyTooltip side="bottom" align="start">
          <button
            type="button"
            :class="[iconButtonClass, '-ml-1.5']"
            :aria-label="`Back to ${trailNames[trailNames.length - 2]}`"
            data-testid="record-pane-back"
            @click="trail?.back()"
          >
            <ArrowLeftIcon class="size-4" aria-hidden="true" />
          </button>
          <template #content>Back (Esc)</template>
        </LazyTooltip>
        <template v-for="(name, index) in trailNames.slice(0, -1)" :key="index">
          <button
            type="button"
            class="min-w-0 max-w-[12rem] shrink truncate rounded underline-offset-2 hover:text-foreground hover:underline"
            :title="`Back to ${name}`"
            data-testid="record-pane-trail-step"
            @click="trail?.goTo(index - 1)"
          >
            {{ name }}
          </button>
          <span aria-hidden="true" class="shrink-0">›</span>
        </template>
        <span
          class="min-w-0 truncate font-medium text-foreground"
          :title="trailNames[trailNames.length - 1]"
          aria-current="page"
          data-testid="record-pane-trail-current"
        >{{ trailNames[trailNames.length - 1] }}</span>
      </nav>
      <component
        :is="headerComponent"
        v-if="headerComponent"
        :key="`header-${recordKey}`"
        class="min-w-0"
        :row="currentStep || isSchedule ? null : row"
        :entity-id="shownEntityId"
        :record-id="shownRecordId"
        size="title"
      />
      <h2 v-else-if="currentStep" class="text-xl leading-tight font-semibold" data-testid="record-pane-heading">{{ currentStep.title }}</h2>
      <div ref="actionsElement" class="absolute top-0 right-0 flex items-center gap-1" data-testid="split-pane-actions">
        <template v-if="!isSchedule">
          <!-- Open in Admin app, Copy ID and Customize page…, as on the record page. -->
          <RecordMoreMenu
            :key="`more-${recordKey}`"
            ref="moreMenu"
            :entity-id="shownEntityId"
            :record-id="shownRecordId"
            :noun="headingNoun"
            :can-customize="Boolean(layout)"
            size="compact"
            test-id-prefix="split-pane-more"
            @customize="customizeOpen = true"
          />
          <LazyTooltip v-if="hasFullPage" side="bottom">
            <button
              type="button"
              data-testid="split-pane-open"
              aria-label="Open the full page"
              :class="iconButtonClass"
              @click="openFullPage"
            >
              <FullPageIcon class="size-4" />
            </button>
            <template #content>Open the full page</template>
          </LazyTooltip>
        </template>
        <LazyTooltip v-if="closable" side="bottom" align="end">
          <button
            type="button"
            data-testid="record-pane-close"
            aria-label="Close"
            :class="iconButtonClass"
            @click="emit('close')"
          >
            <XMarkIcon class="size-4" aria-hidden="true" />
          </button>
          <template #content>{{ escapeCloses && !currentStep ? 'Close (Esc)' : 'Close' }}</template>
        </LazyTooltip>
      </div>
    </div>
    <CustomizeRecordDialog
      v-if="layout && !isSchedule"
      v-model:open="customizeOpen"
      :entity-id="layoutEntityId"
      :noun="noun"
      :columns="columns"
      :layout-state="layout"
      :is-developer="false"
      @closed="moreMenu?.focusTrigger()"
    />
    <!-- A record the pane moved to. It is fetched, since the list holds only its own rows. -->
    <ModelRecordInspector
      v-if="currentStep"
      :key="recordKey"
      :entity-id="currentStep.entityId"
      :record-id="currentStep.recordId"
      :initial-section="currentStep.initialSection ?? null"
      hide-more-menu
    />
    <RecordBody
      v-else-if="!isSchedule && layout"
      :key="recordKey"
      compact
      :entity-id="entityId"
      :record-id="shownRecordId"
      :row="row"
      :layout="layout.shown.value"
    />
    <ModelRecordInspector
      v-else-if="isSchedule"
      :key="recordKey"
      :entity-id="CUSTOM_LIST_ENTITY_ID"
      :record-id="scheduleListId!"
      initial-section="schedules"
    />
    <component
      :is="fallbackComponent"
      v-else
      :key="recordKey"
      v-bind="fallbackProps"
    />
  </div>
</template>
